import type { KetQuaTrangThai, MucDo, Tang } from './types.ts';

/**
 * Hợp đồng của nhóm `/api/v1/m/audit/vet` — dòng chảy token: trace → bước → luồng.
 *
 * ── VÌ SAO KHÔNG NHẬP VÀO `types.ts` ─────────────────────
 * Tệp gốc của module mô tả MỘT sự kiện nhật ký (mục 5.10): nó có `nguoi_thuc_hien`,
 * `doi_tuong`, `ngu_canh` dưới dạng đối tượng lồng. Nhóm `vet` trả về bản ghi
 * ĐÃ GOM, phẳng, tên cột bám đúng SQL (`actor_ten`, `doi_tuong_loai`) vì nó là
 * kết quả của `GROUP BY`, không phải một sự kiện. Nhồi hai hình dạng vào một
 * kiểu sẽ cho ra một đống trường tuỳ chọn mà không chỗ nào dùng hết, và chỗ
 * hỏng sẽ là một màn đọc `su_kien.nguoi_thuc_hien.ten` trên bản ghi gom —
 * `undefined` chạy im lặng tới khi hiện ra ô trống.
 */

export type SapXep = 'moi_nhat' | 'ton_nhat';

/** Tình trạng tính giá vốn của một lượt — chỉ cổng nền tảng lọc và hiện. */
export type TrangThaiGia = 'da_tinh' | 'chua_co_gia';

/**
 * Bảy loại token là một PHÂN HOẠCH của `tong_token` (mục 7.3), và thứ tự ở đây
 * là thứ tự hiện lên: ba loại đầu vào, hai loại đệm, rồi đầu ra và suy luận.
 *
 * Giữ thành mảng `const` chứ không chỉ một union: giao diện phải duyệt đủ bảy
 * loại **kể cả loại nhà cung cấp không báo** — chỉ hiện những khoá có mặt trong
 * JSON thì một lượt gọi thiếu `ghi_dem` trông y như một lượt gọi có `ghi_dem = 0`.
 */
export const LOAI_TOKEN = [
  'dau_vao',
  'dau_vao_anh',
  'dau_vao_tool',
  'dau_vao_dem',
  'ghi_dem',
  'dau_ra',
  'suy_luan',
] as const;

export type LoaiToken = (typeof LOAI_TOKEN)[number];

/**
 * `null` ở một loại nghĩa là **nhà cung cấp không báo**, khác hẳn `0` ("có đo,
 * bằng không"). Kiểu để `number | null` chứ không `number` để chỗ nào quên
 * phân biệt thì trình biên dịch nhắc.
 */
export type BocToken = Record<string, number | null>;

/**
 * Token Fox của một dòng hay một số tổng hợp (ADR-0123 mục 1, 5): đơn vị duy nhất tenant thấy. `null` là dòng ghi trước
 * khi có token Fox, giao diện hiện "—", không hiện 0.
 */
export interface SoFox {
  fox_dau_vao: number | null;
  fox_dau_ra: number | null;
  fox_tokens: number | null;
}

/**
 * Trường chỉ API nền tảng (`/api/v1/audit/ai-usage/*`) trả: token thật, giá vốn, tình trạng tính giá vốn. Mặt module
 * của tenant không có, nên component chỉ đọc chúng khi `useNguonVet().nenTang`.
 */
export interface CotNenTang {
  /** Tenant của dòng — `trace_id` chỉ duy nhất trong một tenant. */
  tenant_id?: string;
  /** Tổng token thật của nhà cung cấp. */
  tong_token?: number | null;
  /** Giá vốn, chỉ cho người có `AI_PRICING.READ`. */
  chi_phi_von?: string | null;
  /** Mã tiền của giá vốn. */
  don_vi_tien?: string | null;
  /** CÓ THỂ ÂM (QĐ-15): đệm đọc lại đắt hơn đọc mới là một kết quả thật. */
  tiet_kiem_dem?: string | null;
}

export interface DongTrace extends CotNenTang, SoFox {
  trace_id: string;
  bat_dau: string;
  ket_thuc: string;
  so_buoc: number;
  so_loi: number;
  so_luot_llm: number;
  so_nhay_cam: number;
  cac_service: string[] | null;
  actor_id: string | null;
  actor_ten: string | null;
  actor_don_vi_id: string | null;
  doi_tuong_loai: string | null;
  doi_tuong_id: string | null;
  doi_tuong_ten: string | null;
  hanh_dong_dau: string | null;
  so_luot_tinh_tien: number | null;
  /** Chỉ nền tảng: một phần lượt gọi chưa có giá vốn. Tách theo việc phải làm: thiếu tỷ giá hay thiếu dòng giá. */
  co_chua_co_gia?: boolean | null;
  co_thieu_ty_gia?: boolean | null;
  co_thieu_dong_gia?: boolean | null;
  /** `false` là chuỗi có lượt mang cảnh báo quy đổi token thật. Tenant vẫn nhận nhưng giao diện không hiện. */
  dang_tin?: boolean | null;
  cac_mo_hinh: string[] | null;
  keo_dai_ms: number | null;
}

export interface BuocVet extends CotNenTang, SoFox {
  /** UUID (chuỗi) ở platform — native là `bigint`. */
  id: string;
  event_id: string;
  thoi_gian: string;
  trace_id: string | null;
  service: string;
  tang: Tang;
  hanh_dong: string;
  actor_loai: string;
  actor_id: string | null;
  actor_ten: string | null;
  actor_don_vi_id: string | null;
  doi_tuong_loai: string | null;
  doi_tuong_id: string | null;
  doi_tuong_ten: string | null;
  ket_qua: KetQuaTrangThai;
  muc_do: MucDo;
  thay_doi: Record<string, unknown>;
  du_lieu_them: Record<string, unknown>;
  /** `null` = bên gửi không khai. Không được hiện thành `0 ms`. */
  do_tre_ms: number | null;
  ma_loi: string | null;
  ly_do: string | null;
  /** Khoảng cách từ lúc chuỗi bắt đầu — trục hoành của waterfall. */
  lech_ms: number;
  mo_hinh: string | null;
  nha_cung_cap: string | null;
  kieu_api: string | null;
  tinh_nang: string | null;
  /** Chỉ nền tảng: bảy loại token thật. */
  token?: BocToken | null;
  uoc_tinh?: boolean | null;
  trang_thai_gia?: TrangThaiGia | null;
  /** `chua_co_gia` vì thiếu tỷ giá, không vì thiếu dòng giá. */
  thieu_ty_gia?: boolean | null;
  canh_bao_token?: string[] | null;
}

export interface ChuoiVet extends SoFox {
  trace_id: string;
  so_buoc: number;
  bat_dau: string | null;
  ket_thuc: string | null;
  keo_dai_ms: number;
  /** Chỉ nền tảng: tổng token thật. */
  tong_token?: number;
  bi_cat: boolean;
  /** `null` khi bộ triển khai chưa cấu hình Langfuse → ẩn nút, không link chết. */
  lien_ket_langfuse: string | null;
  buoc: BuocVet[];
}

export interface LuotGoi extends CotNenTang, SoFox {
  event_id: string;
  thoi_gian: string;
  trace_id: string | null;
  service: string;
  tinh_nang: string | null;
  don_vi_id: string | null;
  nguoi_dung_id: string | null;
  nha_cung_cap: string | null;
  mo_hinh: string | null;
  kieu_api: string | null;
  /** Chỉ nền tảng: bảy loại token thật. */
  token?: BocToken | null;
  uoc_tinh?: boolean | null;
  /** Mã kỹ thuật (`tong_token_lech_bay_loai`…). Tenant vẫn nhận nhưng giao diện không in: chỉ cổng nền tảng hiện. */
  canh_bao?: string[] | null;
  trang_thai?: TrangThaiGia | null;
  /** `chua_co_gia` vì thiếu tỷ giá, không vì thiếu dòng giá. */
  thieu_ty_gia?: boolean | null;
  actor_ten: string | null;
  ket_qua: KetQuaTrangThai | null;
  doi_tuong_loai: string | null;
  doi_tuong_id: string | null;
  doi_tuong_ten: string | null;
  do_tre_ms: number | null;
  nhan_tinh_nang: string | null;
}

export interface DongLuong extends CotNenTang, SoFox {
  doi_tuong_loai: string | null;
  doi_tuong_id: string | null;
  doi_tuong_ten: string | null;
  bat_dau: string;
  ket_thuc: string;
  so_buoc: number;
  so_trace: number;
  so_luot_llm: number;
  so_loi: number;
  so_nguoi: number;
  cac_service: string[] | null;
  cac_trace: string[] | null;
  co_chua_co_gia?: boolean | null;
  co_thieu_ty_gia?: boolean | null;
  co_thieu_dong_gia?: boolean | null;
}

/**
 * `tu`/`den` để BẮT BUỘC ở kiểu dù server có mặc định.
 *
 * Mặc định của server là "24 giờ qua tính từ lúc nhận yêu cầu" — tức một khoảng
 * TRƯỢT. Để trống thì hai lượt gọi cách nhau một phút cho hai tập dữ liệu khác
 * nhau mà khoá truy vấn vẫn như cũ, và danh sách cuộn vô hạn sẽ ghép hai khoảng
 * lệch nhau thành một. `views/SuDungAiView.tsx` đã quy khoảng ra hai mốc tuyệt
 * đối bằng `thamSoKhoang`; kiểu ở đây giữ cho không ai bỏ bước ấy.
 */
export interface TruyVanTrace {
  tu: string;
  den: string;
  service?: string;
  actor_id?: string;
  doi_tuong_loai?: string;
  doi_tuong_id?: string;
  ket_qua?: KetQuaTrangThai;
  tu_khoa?: string;
  sap_xep?: SapXep;
  con_tro?: string;
  gioi_han?: number;
}

export interface TruyVanLuotGoi {
  tu: string;
  den: string;
  mo_hinh?: string;
  nha_cung_cap?: string;
  service?: string;
  tinh_nang?: string;
  nguoi_dung_id?: string;
  don_vi_id?: string;
  trang_thai?: TrangThaiGia;
  co_canh_bao?: boolean;
  trace_id?: string;
  sap_xep?: SapXep;
  con_tro?: string;
  gioi_han?: number;
}

export interface TruyVanLuong {
  tu: string;
  den: string;
  doi_tuong_loai?: string;
  gioi_han?: number;
}

export interface TrangTrace {
  du_lieu: DongTrace[];
  con_nua: boolean;
  /**
   * NGUỒN SỰ THẬT cho "còn trang nữa". `null` ở chế độ `ton_nhat` — nhánh ấy là
   * bảng xếp hạng một trang, không phải danh sách cuộn.
   */
  con_tro_tiep: string | null;
}

export interface TrangLuotGoi {
  du_lieu: LuotGoi[];
  con_nua: boolean;
  con_tro_tiep: string | null;
}

export interface KetQuaLuong {
  du_lieu: DongLuong[];
}

/** Một mã kèm nhãn chụp lúc ghi; `nhan === null` khi bản ghi không mang nhãn. */
export interface MaKemNhan {
  ma: string;
  nhan: string | null;
}

export interface IdKemNhan {
  id: string;
  nhan: string | null;
}

/**
 * `GET /api/v1/m/audit/vet/bo-loc` — giá trị CÓ THẬT trong khoảng cho các ô lọc của màn Sử dụng AI.
 * Cùng lối `/bo-loc` của nhật ký: một id đổi tên ra hai dòng, mỗi nhóm cắt ở 500 (`bi_cat`).
 */
export interface TuyChonLocAi {
  mo_hinh: string[];
  nha_cung_cap: string[];
  service: string[];
  tinh_nang: MaKemNhan[];
  nguoi: IdKemNhan[];
  don_vi: IdKemNhan[];
  bi_cat: boolean;
}
